import { useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { motion } from 'motion/react';
import { mqTabletMin } from 'styled/tools/media-queries';
import { BodySmReg } from '@seed-health/tokens';
import CheckmarkIcon from './CheckmarkIcon';
import AlertCircleIcon from './AlertCircleIcon';

const Wrapper = styled(motion.div)`
  position: fixed;
  bottom: 16px;
  left: 16px;
  right: 16px;
  z-index: 10000;

  @media ${mqTabletMin} {
    left: auto;
    right: 17px;
    bottom: 19px;
    width: 343px;
  }
`;

const Container = styled.div<{ $variant: SnackbarVariant }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px;
  border-radius: 16px;
  background-color: ${({ $variant }) =>
    $variant === 'error' ? '#FFF6F5' : 'var(--fill-primary)'};
  box-shadow: 0px 4px 30px 0px rgba(0, 0, 0, 0.08);

  @media ${mqTabletMin} {
    background-color: ${({ $variant }) =>
      $variant === 'error' ? '#FFF6F5' : 'rgba(252, 252, 247, 0.8)'};
    backdrop-filter: blur(8px);
  }
`;

const IconWrapper = styled.div`
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Message = styled.p`
  ${BodySmReg};
  color: var(--fill-brand);
  margin: 0;
  padding: 0 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
`;

export type SnackbarVariant = 'success' | 'error';

type SnackbarProps = {
  message: string;
  variant?: SnackbarVariant;
  duration?: number;
  onDismiss: () => void;
};

const Snackbar = ({
  message,
  variant = 'success',
  duration = 5000,
  onDismiss,
}: SnackbarProps) => {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const remainingRef = useRef(duration);
  const startTimeRef = useRef(Date.now());

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    clearTimer();
    startTimeRef.current = Date.now();
    timerRef.current = setTimeout(onDismiss, remainingRef.current);
  }, [onDismiss, clearTimer]);

  const pauseTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimer();
      remainingRef.current = Math.max(
        0,
        remainingRef.current - (Date.now() - startTimeRef.current),
      );
    }
  }, [clearTimer]);

  useEffect(() => {
    startTimer();
    return clearTimer;
  }, [startTimer, clearTimer]);

  return (
    <Wrapper
      role='status'
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 12 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      onMouseEnter={pauseTimer}
      onMouseLeave={startTimer}
      onFocus={pauseTimer}
      onBlur={startTimer}
      tabIndex={-1}
    >
      <Container $variant={variant}>
        <IconWrapper>
          {variant === 'error' ? <AlertCircleIcon /> : <CheckmarkIcon />}
        </IconWrapper>
        <Message>{message}</Message>
      </Container>
    </Wrapper>
  );
};

export default Snackbar;
